import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from './SEOHead';

const { FiArrowLeft, FiArrowRight, FiEdit3, FiImage, FiEye, FiShare2, FiUploadCloud } = FiIcons;

const journey = [
  { icon: FiEdit3, t: 'Write in your voice', d: 'A focused editor with live style feedback and ideas measured from your own prose.', to: '/writing-studio', cta: 'Writing Studio' },
  { icon: FiImage, t: 'Design the cover', d: 'Five cover directions for your genre — preview each as a real 3D book.', to: '/cover-studio', cta: 'Cover Studio' },
  { icon: FiEye, t: 'Preview everywhere', d: 'See it as a Kindle, a paperback, an audiobook and a print-ready PDF before you ship.', to: '/book-previewer', cta: 'Book Previewer' },
  { icon: FiShare2, t: 'Market it', d: "We don't just publish — we turn your book into on-brand social posts, scheduled for you.", to: '/book-marketing', cta: 'Book Marketing' },
  { icon: FiUploadCloud, t: 'Publish KDP-ready', d: 'Export clean, validated files — accepted on the first upload.', to: '/signup', cta: 'Get started' },
];

const personas = [
  {
    name: 'Elena', role: 'Literary novelist', image: '/authors/literary-fiction.jpg',
    scenario: 'Tried AI once, hated how it flattened her prose, and quit. Voice DNA learns her long, trailing rhythm so suggestions sound like her — and the distraction-free editor keeps her in flow.',
    features: ['Author Voice DNA', 'Focus editor', 'Version history']
  },
  {
    name: 'Marcus', role: 'Thriller series author', image: '/authors/thriller.jpg',
    scenario: 'Pumps out a book a year. Outlines fast, keeps the pacing tight, and his Voice Memory makes sure book four still sounds like book one across the whole series.',
    features: ['Outline + continue', 'Voice consistency', 'KDP Preflight']
  },
  {
    name: 'Linda', role: 'Ghostwriter · short ebooks', image: '/authors/cozy-mystery.jpg',
    scenario: 'Writes focused 10,000-word ebooks. Drafts in her voice, runs KDP Preflight, exports clean, and sells them straight to her Patreon members with a shareable reader link.',
    features: ['Fast drafts', 'Patreon / sell direct', 'Embeddable reader']
  },
  {
    name: 'Tessa', role: 'YA author building a fanbase', image: '/authors/young-adult.jpg',
    scenario: 'Shares a teaser chapter through the embeddable reader on her own site, grows her email list, then drops the finished book to KDP — all from one place.',
    features: ['Embeddable reader', 'Share drafts', 'One-click export']
  },
  {
    name: 'David', role: 'Memoirist', image: '/authors/memoir.jpg',
    scenario: 'His story is personal. Private by default — his manuscript and Voice Memory are never shared or used to train any model — and the AI helps him find the words without taking over.',
    features: ['Private by default', 'Author Voice DNA', 'Co-author / editor']
  },
  {
    name: 'Ray', role: 'Business nonfiction author', image: '/authors/business-nonfiction.jpg',
    scenario: 'Knows his material, hates formatting. Drafts fast in his own crisp voice, runs live KDP Preflight, and ships a clean book without hiring a designer.',
    features: ['Fast drafting', 'KDP Preflight', 'Clean export']
  }
];

const ForAuthorsPage = () => {
  const structuredData = {
    '@context': 'https://schema.org', '@type': 'WebPage',
    name: 'ManuscriptSmith for Authors',
    description: 'How different indie authors — novelists, ghostwriters, series authors, memoirists, and nonfiction writers — use ManuscriptSmith.',
    url: 'https://manuscriptsmith.com/for-authors'
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-parchment-50 to-parchment-100">
      <SEOHead
        title="For Authors — How Writers Like You Use ManuscriptSmith"
        description="Novelists, ghostwriters, series authors, memoirists, and nonfiction writers use ManuscriptSmith to write in their own voice, stay KDP-ready, and publish and sell. See how."
        keywords="ai writing for authors, indie author tools, ghostwriter software, series author, self-publishing workflow"
        canonicalUrl="https://manuscriptsmith.com/for-authors"
        structuredData={structuredData}
      />

      <main>
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Built for how <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">you</span> actually write
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            A novelist, a ghostwriter, and a memoirist don't write the same way - and they shouldn't use the same
            blended AI. Here's how different authors put ManuscriptSmith to work.
          </p>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {personas.map((p, i) => (
              <motion.div key={p.name} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex flex-col"
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}>
                <div className="flex items-center gap-3 p-5 border-b border-gray-100">
                  <img src={p.image} alt={`${p.role}`} loading="lazy" className="w-14 h-14 rounded-full object-cover ring-2 ring-secondary-200" />
                  <div>
                    <div className="font-bold text-gray-900">{p.name}</div>
                    <div className="text-sm text-secondary-700">{p.role}</div>
                  </div>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed p-5 flex-1">{p.scenario}</p>
                <div className="px-5 pb-5 flex flex-wrap gap-2">
                  {p.features.map((f) => (
                    <span key={f} className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-primary-50 text-primary-700">{f}</span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* End-to-end author journey */}
        <section className="bg-white border-y border-parchment-200 py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">One studio, blank page to bestseller list</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">Every step of the journey lives in one place — and each one is built to sound and look like <em>your</em> book.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
              {journey.map((s, i) => (
                <motion.div key={s.t} className="relative bg-parchment-50 rounded-2xl border border-parchment-200 p-5 flex flex-col"
                  initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.08 }}>
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-r from-primary-500 to-secondary-500 flex items-center justify-center mb-3">
                    <SafeIcon icon={s.icon} className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-[11px] font-bold text-gray-400 mb-1">STEP {i + 1}</div>
                  <h3 className="font-bold text-gray-900 mb-1.5">{s.t}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed flex-1">{s.d}</p>
                  <Link to={s.to} className="mt-3 inline-flex items-center text-sm font-semibold text-primary-700 hover:text-primary-800">
                    {s.cta} <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-1" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Your Author AI band */}
        <section className="py-16 bg-gradient-to-br from-emerald-50 to-stone-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Writing memoir or fiction from your life?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto mb-6">
              Your Author AI learns your real timeline, people and places from anything you share — and its Privacy
              Vault keeps sensitive details redacted from every AI until you personally authorize each one.
            </p>
            <Link to="/author-ai" className="inline-flex items-center px-6 py-3 bg-emerald-700 text-white font-semibold rounded-lg hover:bg-emerald-800">
              Meet your Author AI <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl p-10 text-center text-white shadow-xl">
            <h2 className="text-3xl font-bold mb-4">However you write, write it like yourself</h2>
            <p className="text-lg opacity-90 mb-8">Build your Voice Memory and see your own style come back to you.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/signup" className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 font-bold text-lg rounded-lg hover:bg-gray-50">
                Get started <SafeIcon icon={FiArrowRight} className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/editor" className="inline-flex items-center justify-center px-8 py-4 bg-white/15 border border-white/40 text-white font-semibold text-lg rounded-lg hover:bg-white/25">
                Try the live editor
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ForAuthorsPage;
